import { type FC } from 'react'
import { Button } from '../primitives/index.js'
import { useMounted } from '../../hooks/index.js'
import type { ChildrenProps } from './SwapWidgetRenderer.js'
import { EventNames } from '../../constants/events.js'
import { cn } from '../../utils/cn.js'
import { useHapticEvent } from '../../providers/RelayKitProvider.js'

type SwapButtonProps = {
  transactionModalOpen: boolean
  depositAddressModalOpen: boolean
  onConnectWallet?: () => void
  onAnalyticEvent?: (eventName: string, data?: any) => void
  onClick: () => void
  context: 'Swap' | 'Deposit' | 'Withdraw' | 'Buy' | 'Sell'
  showHighPriceImpactWarning?: boolean
  disableSwapButton?: boolean
  tokenWidgetMode?: boolean
  hasValidAmount?: boolean
} & Pick<
  ChildrenProps,
  | 'quote'
  | 'address'
  | 'hasInsufficientBalance'
  | 'isInsufficientLiquidityError'
  | 'debouncedInputAmountValue'
  | 'debouncedOutputAmountValue'
  | 'isSameCurrencySameRecipientSwap'
  | 'ctaCopy'
  | 'isValidFromAddress'
  | 'isValidToAddress'
  | 'fromChainWalletVMSupported'
  | 'recipientWalletSupportsChain'
  | 'isFetchingQuote'
>

const SwapButton: FC<SwapButtonProps> = ({
  transactionModalOpen,
  depositAddressModalOpen,
  isValidFromAddress,
  isValidToAddress,
  context,
  showHighPriceImpactWarning = false,
  disableSwapButton = false,
  tokenWidgetMode = false,
  hasValidAmount = true,
  onConnectWallet,
  quote,
  address,
  hasInsufficientBalance,
  isInsufficientLiquidityError,
  debouncedInputAmountValue,
  debouncedOutputAmountValue,
  isSameCurrencySameRecipientSwap,
  fromChainWalletVMSupported,
  recipientWalletSupportsChain,
  onClick,
  ctaCopy,
  onAnalyticEvent,
  isFetchingQuote
}) => {
  const isMounted = useMounted()
  const haptic = useHapticEvent()

  if (isMounted && (address || !fromChainWalletVMSupported)) {
    const invalidAmount =
      !quote ||
      Number(debouncedInputAmountValue) === 0 ||
      Number(debouncedOutputAmountValue) === 0

    const isWalletSelectionPrompt =
      tokenWidgetMode &&
      (ctaCopy.includes('Select') || ctaCopy.includes('Enter'))
    const isSelectTokenPrompt = tokenWidgetMode && ctaCopy === 'Select a token'

    const buttonDisabled = tokenWidgetMode
      ? disableSwapButton ||
        isFetchingQuote ||
        isSelectTokenPrompt ||
        hasInsufficientBalance ||
        (!isWalletSelectionPrompt && !hasValidAmount)
      : isFetchingQuote ||
        (isValidToAddress &&
          (isValidFromAddress || !fromChainWalletVMSupported) &&
          (invalidAmount ||
            hasInsufficientBalance ||
            isInsufficientLiquidityError ||
            transactionModalOpen ||
            depositAddressModalOpen ||
            isSameCurrencySameRecipientSwap ||
            !recipientWalletSupportsChain ||
            disableSwapButton))

    const buttonLabel =
      tokenWidgetMode && isFetchingQuote && hasValidAmount
        ? 'Fetching quote'
        : ctaCopy

    return (
      <Button
        className={cn(
          'relay:justify-center relay:w-full',
          !tokenWidgetMode && 'relay:normal-case'
        )}
        color={showHighPriceImpactWarning ? 'error' : 'primary'}
        aria-label={context}
        cta={true}
        disabled={buttonDisabled}
        data-testid={tokenWidgetMode ? 'token-action-button' : 'swap-button'}
        onClick={() => {
          if (!buttonDisabled) {
            haptic('medium')
            onClick()
          }
        }}
      >
        {buttonLabel}
      </Button>
    )
  }

  return (
    <Button
      cta={true}
      className={cn(
        'relay:justify-center relay:w-full',
        !tokenWidgetMode && 'relay:normal-case'
      )}
      aria-label="Connect wallet"
      onClick={() => {
        if (!onConnectWallet) {
          throw 'Missing onWalletConnect function'
        }

        haptic('medium')
        onConnectWallet()
        onAnalyticEvent?.(EventNames.CONNECT_WALLET_CLICKED, {
          context
        })
      }}
      data-testid={
        tokenWidgetMode
          ? 'token-widget-connect-wallet-button'
          : 'widget-connect-wallet-button'
      }
    >
      Connect Wallet
    </Button>
  )
}

export default SwapButton
